/**
 * The bit-sea backdrop: one fragment shader into a half-resolution
 * framebuffer, upscaled onto the canvas. Seals inject ripples (up to 16) as
 * uniforms. When the ladder makes it static, or motion is reduced, it renders
 * once and is only copied after that.
 *
 * `rippleUniforms` and `litColor` are pure and tested; the pass is a thin
 * adapter.
 */

import type { FrameModel } from '../contracts';
import { CAM } from './camera';
import type { GlKit } from './gl';
import { luminance, mixRgb, type Rgb, type Theme } from './palette';
import fullscreen from '../shaders/fullscreen.vert.glsl' with { type: 'text' };
import sea from '../shaders/backdrop.frag.glsl' with { type: 'text' };
import copy from '../shaders/copy.frag.glsl' with { type: 'text' };

export const CELL_CSS = 24;             // one micro-bit, CSS px
export const RIPPLE_SPEED_CSS = 380;    // a ripple front, CSS px per second
export const MAX_RIPPLES = 16;
export const RIPPLE_FLOATS = 8;         // x, y (viewport px), age (s), hue, r, g, b, width (px)
export const SEA_UNIFORMS = ['u_res', 'u_cell', 'u_time', 'u_offset', 'u_bg', 'u_lit', 'u_vignette', 'u_speed', 'u_far', 'u_rip', 'u_ripc', 'u_ripN'] as const;
export const COPY_UNIFORMS = ['u_tex'] as const;
export const BACKDROP_SOURCES = { fullscreen, sea, copy } as const;

/**
 * A lit cell's colour: toward the door's teal on dark, toward ink on light,
 * but never more than 6 % luminance away from the ground, so text over the
 * sea keeps its contrast.
 */
const litCache = new WeakMap<Theme, Rgb>();
export const litColor = (theme: Theme): Rgb => {
  const hit = litCache.get(theme);
  if (hit) return hit;
  const toward = theme.dark ? theme.mine : theme.ink;
  const base = luminance(theme.bg);
  let lo = 0, hi = 0.6;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (Math.abs(luminance(mixRgb(theme.bg, toward, mid)) - base) <= 0.06) lo = mid; else hi = mid;
  }
  const lit = mixRgb(theme.bg, toward, lo);
  litCache.set(theme, lit);
  return lit;
};

/**
 * FrameModel ripples → the shader's two vec4 arrays, in framebuffer px with y
 * up. `scale` is framebuffer px per CSS px (DPR / 2 at half resolution).
 */
export const rippleUniforms = (frame: Pick<FrameModel, 'ripples' | 'rippleCount' | 'camera'>, scale: number, out?: { a: Float32Array; b: Float32Array }): { a: Float32Array; b: Float32Array; n: number } => {
  const a = out?.a ?? new Float32Array(MAX_RIPPLES * 4);
  const b = out?.b ?? new Float32Array(MAX_RIPPLES * 4);
  const left = frame.camera[CAM.canvasLeft] ?? 0, top = frame.camera[CAM.canvasTop] ?? 0, H = frame.camera[CAM.canvasH] ?? 0;
  const n = Math.min(MAX_RIPPLES, frame.rippleCount);
  for (let i = 0; i < n; i++) {
    const o = i * RIPPLE_FLOATS;
    const x = (frame.ripples[o] ?? 0) - left, y = (frame.ripples[o + 1] ?? 0) - top;
    a[i * 4] = x * scale;
    a[i * 4 + 1] = (H - y) * scale;
    a[i * 4 + 2] = frame.ripples[o + 2] ?? 0;
    const width = frame.ripples[o + 7] ?? 60;   // negative: a ring closing in
    a[i * 4 + 3] = Math.sign(width || 1) * Math.max(8, Math.abs(width) * scale);
    b[i * 4] = frame.ripples[o + 4] ?? 0;
    b[i * 4 + 1] = frame.ripples[o + 5] ?? 0;
    b[i * 4 + 2] = frame.ripples[o + 6] ?? 0;
    b[i * 4 + 3] = 1;
  }
  return { a, b, n };
};

export interface BackdropPass {
  resize(w: number, h: number, dpr: number): void;
  /** Render the sea (unless static and already drawn) and copy it to the canvas; returns draw calls. */
  draw(frame: FrameModel, theme: Theme, opts: { animate: boolean; viewport: [number, number] }): number;
  invalidate(): void;
  destroy(): void;
}

export const createBackdropPass = (gl: WebGL2RenderingContext, kit: GlKit): BackdropPass => {
  const seaProg = kit.link(fullscreen, sea, 'backdrop');
  const copyProg = kit.link(fullscreen, copy, 'copy');
  const us = kit.uniforms(seaProg, SEA_UNIFORMS);
  const uc = kit.uniforms(copyProg, COPY_UNIFORMS);
  const vao = gl.createVertexArray();
  let tex: WebGLTexture | null = null;
  let fbo: WebGLFramebuffer | null = null;
  let fw = 1, fh = 1, dpr = 1;
  let stale = true;
  const rip = { a: new Float32Array(MAX_RIPPLES * 4), b: new Float32Array(MAX_RIPPLES * 4) };

  const alloc = () => {
    if (tex) gl.deleteTexture(tex);
    if (fbo) gl.deleteFramebuffer(fbo);
    tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, fw, fh, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    stale = true;
  };
  alloc();

  return {
    resize(w, h, d) {
      const nw = Math.max(1, Math.ceil(w / 2)), nh = Math.max(1, Math.ceil(h / 2));
      dpr = d;
      if (nw === fw && nh === fh && tex) return;
      fw = nw;
      fh = nh;
      alloc();
    },
    draw(frame, theme, opts) {
      let calls = 0;
      gl.bindVertexArray(vao);
      if (opts.animate || stale) {
        const scale = dpr / 2;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.viewport(0, 0, fw, fh);
        gl.useProgram(seaProg);
        const lit = litColor(theme);
        gl.uniform2f(us.u_res, fw, fh);
        gl.uniform1f(us.u_cell, CELL_CSS * scale);
        gl.uniform1f(us.u_time, opts.animate ? frame.camera[CAM.time] ?? 0 : 0);
        gl.uniform2f(us.u_offset, (frame.camera[CAM.parallaxX] ?? 0) * scale, -(frame.camera[CAM.parallaxY] ?? 0) * scale);
        gl.uniform3f(us.u_bg, theme.bg[0], theme.bg[1], theme.bg[2]);
        gl.uniform3f(us.u_lit, lit[0], lit[1], lit[2]);
        gl.uniform1f(us.u_vignette, theme.dark ? 0.6 : 0.25);
        gl.uniform1f(us.u_speed, RIPPLE_SPEED_CSS * scale);
        gl.uniform1f(us.u_far, Math.hypot(fw, fh));
        const r = opts.animate ? rippleUniforms(frame, scale, rip) : { ...rip, n: 0 };
        gl.uniform4fv(us.u_rip, r.a);
        gl.uniform4fv(us.u_ripc, r.b);
        gl.uniform1i(us.u_ripN, r.n);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        calls++;
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        stale = false;
      }
      gl.viewport(0, 0, opts.viewport[0], opts.viewport[1]);
      gl.useProgram(copyProg);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(uc.u_tex, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      calls++;
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.bindVertexArray(null);
      return calls;
    },
    invalidate() {
      stale = true;
    },
    destroy() {
      if (tex) gl.deleteTexture(tex);
      if (fbo) gl.deleteFramebuffer(fbo);
      gl.deleteVertexArray(vao);
      gl.deleteProgram(seaProg);
      gl.deleteProgram(copyProg);
    },
  };
};
